import React, { useState, useEffect } from "react";
import Modal from "../../../../components/Modal";

const MAX_WORDS = 50;
const MAX_LINES = 5;

const countWords = (text) =>
  text.trim() === "" ? 0 : text.trim().split(/\s+/).length;

const countLines = (text) => (text === "" ? 1 : text.split("\n").length);

const validateReason = (text) => {
  if (!text.trim()) return "Reason is required.";
  if (countWords(text) > MAX_WORDS)
    return `Maximum ${MAX_WORDS} words allowed (currently ${countWords(text)}).`;
  if (countLines(text) > MAX_LINES)
    return `Maximum ${MAX_LINES} lines allowed (currently ${countLines(text)}).`;
  return null;
};

/**
 * Per-document deficiency modal.
 * Limit: 50 words and 5 lines maximum.
 */
const DocDeficiencyModal = ({
  isOpen,
  docName,
  existingReason,
  onClose,
  onSubmit,
}) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setReason(existingReason || "");
      setError("");
    }
  }, [isOpen, existingReason]);

  const words = countWords(reason);
  const lines = countLines(reason);
  const wordsOver = words > MAX_WORDS;
  const linesOver = lines > MAX_LINES;
  const isOverLimit = wordsOver || linesOver;

  const handleChange = (e) => {
    const val = e.target.value;
    setReason(val);
    // Clear error as user types — re-validate live
    const err = validateReason(val);
    setError(err || "");
  };

  // Prevent typing beyond limits — block new words/lines when at limit
  const handleKeyDown = (e) => {
    const val = reason;
    // Block Enter if already at max lines
    if (e.key === "Enter" && countLines(val) >= MAX_LINES) {
      e.preventDefault();
      setError(`Maximum ${MAX_LINES} lines allowed.`);
      return;
    }
    // Block Space/Enter if already at max words (allow backspace/delete/arrows)
    const navigationKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];
    if (
      countWords(val) >= MAX_WORDS &&
      (e.key === " " || e.key === "Enter") &&
      !navigationKeys.includes(e.key)
    ) {
      e.preventDefault();
      setError(`Maximum ${MAX_WORDS} words allowed.`);
    }
  };

  const handleSubmit = () => {
    const err = validateReason(reason);
    if (err) {
      setError(err);
      return;
    }
    onSubmit(reason.trim());
    setReason("");
    setError("");
  };

  const handleClose = () => {
    setReason("");
    setError("");
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Document Deficiency"
      size="small"
    >
      <div className="doc-deficiency-modal">
        {/* Document tag */}
        <div className="doc-deficiency-modal__doc-tag">
          <span className="doc-deficiency-modal__doc-icon">📄</span>
          <span className="doc-deficiency-modal__doc-name">{docName}</span>
          {existingReason && (
            <span className="doc-deficiency-modal__badge">Editing</span>
          )}
        </div>

        {/* Previous reason */}
        {existingReason && (
          <div className="doc-deficiency-modal__prev">
            <span className="doc-deficiency-modal__prev-label">
              Previous reason:
            </span>
            <span className="doc-deficiency-modal__prev-text">
              {existingReason}
            </span>
          </div>
        )}

        {/* Label */}
        <label
          className="doc-deficiency-modal__label"
          htmlFor="doc-deficiency-reason"
        >
          {existingReason ? "Update reason" : "Deficiency reason"}
          <span className="required-mark"> *</span>
        </label>

        {/* Textarea */}
        <textarea
          id="doc-deficiency-reason"
          className={`doc-deficiency-modal__textarea${error ? " input-error" : ""}`}
          rows={5}
          placeholder="Describe the deficiency (max 50 words, 5 lines)..."
          value={reason}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />

        {/* Live counter */}
        {/* <div className="doc-deficiency-modal__counter">
          <span
            className={wordsOver ? "doc-deficiency-modal__counter--over" : ""}
          >
            {words}/{MAX_WORDS} words
          </span>
          <span
            className={linesOver ? "doc-deficiency-modal__counter--over" : ""}
          >
            {lines}/{MAX_LINES} lines
          </span>
        </div> */}

        {/* Error */}
        {error && <span className="input-error-msg">{error}</span>}

        {/* Actions */}
        <div className="doc-deficiency-modal__actions">
          <button type="button" className="btn-cancel" onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-submit-reject"
            onClick={handleSubmit}
            disabled={isOverLimit}
          >
            {existingReason ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DocDeficiencyModal;
